<template>
    <div class="choosePlanTable">
        <div class="pricingTable_mobile_switcher">
            <SwitcherInput :value="billingPeriod === 'yearly'" @input="handleBillingPeriodChange" />
            <p>Pay annually and save up to <span>20%</span> on the plan price</p>
            <button
                v-if="!coupon"
                class="buttonAddPromocode"
                @click="couponModal.open"
            >
                Add promocode
            </button>

            <div v-if="coupon" class="promocode">
                <div class="promocode_body">
                    <strong> Promo code:</strong>
                    <span>{{ coupon }}</span>
                </div>
                <button
                    class="promocode_action"
                    type="button"
                    @click="handleCouponRemove"
                >
                    <IconClose />
                </button>
            </div>
        </div>

        <vue-good-table
            :columns="columns"
            :rows="items"
            :sort-options="{ enabled: false }"
            :row-style-class="rowStyleClassFn"
        >
            <template #table-column="{ column }">
                <div class="pricingTable_title">
                    <div
                        v-if="column.field !== 'empty'"
                        :class="[
                            'pricingTable_title_planColumn',
                            {
                                pricingTable_title_planColumn_with_trial:
                                    column.field === 'starter',
                            },
                        ]"
                    >
                        <div
                            v-if="column[billingPeriod].trialDays"
                            class="pricingTable_title_planColumn_trial"
                        >
                            {{ column[billingPeriod].trialDays }} days free trial
                        </div>
                        <div class="pricingTable_title_planColumn_title">
                            {{ column.label }}
                        </div>
                        <div class="pricingTable_title_planColumn_price">
                            <span>{{ column[billingPeriod].prevCost }}</span>
                            <span>{{ column[billingPeriod].cost }}</span>
                            <span>/ MO</span>
                        </div>
                        <div class="pricingTable_title_planColumn_button">
                            <button
                                type="button"
                                @click="handlePlanChoose(column[billingPeriod].planId)"
                            >
                                Choose
                            </button>
                        </div>
                    </div>

                    <div
                        v-if="column.field === 'empty'"
                        class="pricingTable_title_switcher"
                    >
                        <SwitcherInput :value="billingPeriod === 'yearly'" @input="handleBillingPeriodChange" />
                        <p>
                            Pay annually and save up to <span>20%</span> on the plan price
                        </p>

                        <button
                            v-if="!coupon"
                            class="buttonAddPromocode"
                            @click="couponModal.open"
                        >
                            Add promocode
                        </button>
                        <div v-if="coupon" class="promocode">
                            <div class="promocode_body">
                                <strong> Promo code:</strong>
                                <span>{{ coupon }}</span>
                            </div>
                            <button
                                class="promocode_action"
                                type="button"
                                @click="handleCouponRemove"
                            >
                                <IconClose />
                            </button>
                        </div>
                    </div>
                </div>
            </template>

            <template #table-row="{ row, column }">
                <div
                    v-if="row.isHeadRow && column.field !== 'empty'"
                    class="pricingTable_row_title"
                >
                    <div class="pricingTable_cell">
                        <div class="pricingTable_cell_plan">
                            {{ row[column.field] }}
                        </div>
                    </div>
                </div>

                <div
                    v-if="row.isHeadRow && column.field === 'empty'"
                    class="pricingTable_tableGroup"
                >
                    <div class="pricingTable_row pricingTable_row_title">
                        <div class="pricingTable_cell">
                            <h2>{{ row[column.field] }}</h2>
                        </div>
                    </div>
                </div>

                <div v-if="!row.isHeadRow" class="pricingTable_cell">
                    <h4 v-if="column.field === 'empty'">
                        {{ row[column.field] }}
                    </h4>

                    <div v-if="column.field !== 'empty'">
                        <span v-if="typeof row[column.field] !== 'boolean'">
                            {{ row[column.field] }}
                        </span>

                        <div
                            v-if="typeof row[column.field] === 'boolean'"
                            class="pricingTable_cell_icons"
                        >
                            <IconTickCircle
                                v-if="row[column.field]"
                                class="pricingTable_cell_icons_true"
                            />
                            <IconMinusCircle
                                v-if="!row[column.field]"
                                class="pricingTable_cell_icons_false"
                            />
                        </div>
                    </div>
                </div>
            </template>
        </vue-good-table>

        <vue-final-modal
            v-model="couponModal.state"
            :lock-scroll="true"
            classes="modal-container"
            content-class="modal-content"
        >
            <PromocodeModal
                @handleCloseModal="couponModal.close"
                @handleSavePromocode="handleCouponAdd"
            />
        </vue-final-modal>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useVModel } from '@vueuse/core';
import type { Maybe, OrganizationFormData, SubscriptionPlan, PlanColumn } from '../types';
import { useModal } from '../composables/modals';
import { formatCurrency } from '../composables/currencies';
import 'vue-good-table/dist/vue-good-table.css';
import { VueGoodTable } from 'vue-good-table';
import IconClose from '@/components/icons/IconClose.vue';
import SwitcherInput from '@/components/SwitcherInput.vue';
import PromocodeModal from '@/components/PromocodeModal.vue';
import IconTickCircle from '@/components/icons/IconTickCircle.vue';
import IconMinusCircle from '@/components/icons/IconMinusCirlce.vue';

const props = withDefaults(defineProps<{
    plans?: SubscriptionPlan[];
    value: Maybe<OrganizationFormData['coupon']>;
}>(), {
    plans: () => [],
    value: undefined,
});

const emit = defineEmits<{
    (e: 'input', payload: OrganizationFormData['coupon']): void;
    (e: 'choose', payload: OrganizationFormData['plan_id']): void;
}>();

const columns = computed<PlanColumn[]>(() => {
    const plans = props.plans.reduce<Record<string, PlanColumn>>(function (all, plan) {
        if (!all.hasOwnProperty(plan.name)) {
            all[plan.name] = {
                field: plan.name.toLowerCase(),
                label: plan.name,
                yearly: {
                    cost: '',
                    planId: '',
                    prevCost: null,
                },
                monthly: {
                    cost: '',
                    planId: '',
                    prevCost: null,
                },
            };
        }

        const monthlyPrice = plan.interval === 'monthly' ? plan.raw_price : (plan.raw_price / 12);

        all[plan.name][plan.interval]!.planId = plan.id;
        all[plan.name][plan.interval]!.cost = formatCurrency(Math.round(monthlyPrice));

        if (plan.interval === 'monthly') {
            all[plan.name].yearly!.prevCost = formatCurrency(plan.raw_price);
        }

        if (plan.trial_days) {
            all[plan.name][plan.interval]!.trialDays = plan.trial_days;
        }

        return all;
    }, {
        empty: {
            field: 'empty',
            tdClass: 'moveColumn',
        },
    });

    return Object.values(plans);
});

const items = [
    {
        isHeadRow: true,
        group: 'general',
        empty: 'General features',
        starter: 'Starter',
        professional: 'Professional',
        business: 'Business',
        enterprise: 'Enterprise',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Launching Profile Automation',
        starter: '1',
        professional: '5',
        business: '10',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'eBay Templates',
        starter: '1',
        professional: '10',
        business: '20',
        enterprise: '20',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Synchronizing History',
        starter: '7 Days',
        professional: '30 Days',
        business: '30 Days',
        enterprise: '30 Days',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Users Monthly Conversations',
        starter: '25',
        professional: 'Unlimited',
        business: 'Unlimited',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Seller Accounts',
        starter: '1',
        professional: '3',
        business: '5',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'general',
        empty: 'Orders (Per Month)',
        starter: '300',
        professional: '1000',
        business: '2500',
        enterprise: 'Unlimited',
    },

    {
        isHeadRow: true,
        group: 'management',
        empty: 'Listing management',
        starter: '',
        professional: '',
        business: '',
        enterprise: '',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'Employees Accounts',
        starter: '1',
        professional: '2',
        business: '5',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'Image Hosting',
        starter: '2 MB',
        professional: '5 MB',
        business: '10 MB',
        enterprise: '50 MB',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'Email Templates',
        starter: '1',
        professional: '5',
        business: '20',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'Products',
        starter: '1000',
        professional: '5000',
        business: '20000',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'File uploading size (Excel)',
        starter: '10 MB',
        professional: '100 MB',
        business: '200 MB',
        enterprise: '500 MB',
    },
    {
        isHeadRow: false,
        group: 'management',
        empty: 'File Upload Limits (Rows per file)',
        starter: false,
        professional: false,
        business: false,
        enterprise: false,
    },

    {
        isHeadRow: true,
        group: 'support',
        empty: 'Support',
        starter: '',
        professional: '',
        business: '',
        enterprise: '',
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Chat Templates',
        starter: '1',
        professional: '3',
        business: '5',
        enterprise: 'Unlimited',
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Incoming Orders Frequency',
        starter: '20 Min',
        professional: '15 Min',
        business: '10 Min',
        enterprise: '5 Min',
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Hour Number of Help Sessions',
        starter: '2',
        professional: '5',
        business: '10',
        enterprise: '20',
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Form Builder',
        starter: false,
        professional: true,
        business: true,
        enterprise: true,
    },
    {
        isHeadRow: false,
        group: 'support',
        empty: 'Custom Orders',
        starter: false,
        professional: true,
        business: true,
        enterprise: true,
    },
];

function rowStyleClassFn(row: { isHeadRow: boolean; group: string }) {
    return row.isHeadRow ? 'headRow' : row.group;
}

const billingPeriod = ref<'monthly' | 'yearly'>('yearly');

function handleBillingPeriodChange(val: boolean) {
    billingPeriod.value = val ? 'yearly' : 'monthly';
}

const coupon = useVModel(props, 'value', emit, { eventName: 'input' });
const couponModal = useModal();

function handleCouponAdd(value: OrganizationFormData['coupon']) {
    coupon.value = value;
}

function handleCouponRemove() {
    coupon.value = null;
}

function handlePlanChoose(plan: OrganizationFormData['plan_id']) {
    emit('choose', plan);
}
</script>

<style lang="scss">
.modal-container {
    display: flex;
    justify-content: center;
    align-items: center;
}
.modal-content {
    position: relative;
}

.promocode {
  margin-top: 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;

  &_body {
    width: calc(100% - 52px);

    strong {
      display: block;
      font-family: "ITC Avant Garde Gothic Std", serif;
      font-size: 14px;
      font-style: normal;
      font-weight: 600;
      color: #202020;

      @media screen and (max-width: 1540px) {
        font-size: 10px;
      }
    }
    span {
      font-family: "ITC Avant Garde Gothic Std", serif;
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      color: #bac2cd;
      @media screen and (max-width: 1540px) {
        font-size: 12px;
      }
    }
  }

  &_action {
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    background-color: transparent;
    border: none;
    outline: none;
    svg {
      path {
        stroke: #40acff;
      }
    }
  }
}

.buttonAddPromocode {
  color: #40acff;
  text-align: center;
  font-family: "ITC Avant Garde Gothic Std",serif;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 1;
  padding: 8px;
  border: none;
  outline: none;
  margin: 50px auto 0;
  display: block;
  background-color: transparent;
  cursor: pointer;
  transition: all 0.3s;

  @media screen and (max-width: 1540px) {
    margin: 30px auto 0;
    font-size: 12px;
  }

  &:hover {
    color: #38317f;
  }
}

.moveColumn {
  position: sticky;
  left: 0;
  z-index: 2;
  @media screen and (max-width: 860px) {
    &:after {
      content: "";
      display: inline-block;
      height: 100%;
      pointer-events: none;
      position: absolute;
      right: -36px;
      top: 0;
      width: 36px;
      box-shadow: inset 6px 0 5px -3px #0003;
    }
  }
}
table.vgt-table thead th:nth-child(1) {
  position: sticky;
  top: 0;
  left: 0;
  z-index: 10;
  @media screen and (max-width: 860px) {
    &:after {
      content: "";
      display: inline-block;
      height: 100%;
      pointer-events: none;
      position: absolute;
      right: -36px;
      top: 0;
      width: 36px;
      box-shadow: inset 6px 0 5px -3px #0003;
    }
  }
}
.vgt-table {
  @media screen and (max-width: 860px) {
    width: 900px !important;
    table-layout: fixed !important;
  }
}

.vgt-table.bordered th {
  background: #ffffff;
}

.vgt-inner-wrap {
  box-shadow: none;
  border-radius: 0;
}

table.vgt-table,
.vgt-table.bordered td,
.vgt-table.bordered th {
  padding: 0;
  border: none;
}

.vgt-fixed-header {
  width: 100%;
}
.vgt-inner-wrap td,
.vgt-inner-wrap th {
  width: 20% !important;
  vertical-align: middle;

  @media screen and (max-width: 860px) {
    width: 170px !important;
  }
}

.vgt-table.bordered td,
.vgt-table.bordered th {
  @media screen and (max-width: 900px) {
    max-width: none;
  }
}

table.vgt-table,
.vgt-table.bordered td,
.vgt-table.bordered th {
  vertical-align: middle;
}

.vgt-inner-wrap tbody tr:nth-child(1) {
  .pricingTable_row_title {
    padding-top: 33px;
  }
}

tr {
  &.headRow {
    td {
      background-color: #ffffff;
    }
  }

  &.general,
  &.support {
    td {
      background-color: #fff;
    }
    &:nth-child(2n) td {
      background-color: #f2f4f7;
    }
  }
  &.management {
    &:nth-child(2n) td {
      background-color: #fff;
    }
    &:nth-child(2n + 1) td {
      background-color: #f2f4f7;
    }
  }
}

.pricingTable {
  &_row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 24px 0;

    @media screen and (max-width: 1540px) {
      padding: 26px 0;
    }
    @media screen and (max-width: 1100px) {
      padding: 7px 0;
    }
    @media screen and (max-width: 860px) {
      padding: 10px 0;
    }

    &:nth-child(2n) {
      background-color: #f2f4f7;
    }
    &_title {
      padding-top: 71px;
      padding-bottom: 50px;
      .pricingTable_cell {
        padding: 0;
      }

      @media screen and (max-width: 1540px) {
        padding-bottom: 34px;
        padding-top: 58px;
      }
      @media screen and (max-width: 1100px) {
        padding-bottom: 27px;
      }
      @media screen and (max-width: 860px) {
        padding-bottom: 17px;
      }
    }
  }

  .header_row:nth-child(1) .pricingTable_row_title {
    padding-top: 0;
  }

  &_cell {
    padding: 24px 32px;
    font-family: "ITC Avant Garde Gothic Std",serif;
    font-size: 18px;
    font-style: normal;
    font-weight: 300;
    line-height: 175%;
    text-align: center;

    @media screen and (max-width: 1540px) {
      font-size: 16px;
      padding: 26px 25px;
    }

    @media screen and (max-width: 1100px) {
      padding: 7px 4px;
      font-size: 14px;
    }

    @media screen and (max-width: 860px) {
      padding: 10px 16px;
    }

    h2 {
      font-family: "Bebas Neue Pro",serif;
      font-size: 48px;
      font-style: normal;
      font-weight: 700;
      line-height: 100%;
      text-transform: uppercase;
      color: #202020;
      margin: 0;
      text-align: start;

      @media screen and (max-width: 1540px) {
        font-size: 40px;
      }
      @media screen and (max-width: 1100px) {
        font-size: 24px;
      }
    }
    h4 {
      font-family: "ITC Avant Garde Gothic Std",serif;
      font-size: 18px;
      font-style: normal;
      font-weight: 600;
      line-height: 135%;
      text-transform: capitalize;
      margin: 0;
      text-align: start;

      @media screen and (max-width: 1540px) {
        line-height: 150%;
      }

      @media screen and (max-width: 1100px) {
        font-size: 14px;
      }
      @media screen and (max-width: 860px) {
        font-size: 12px;
      }
    }

    &:nth-child(n + 2) {
      text-align: center;
    }
    &_plan {
      font-family: "Bebas Neue Pro",serif;
      font-size: 28px;
      font-style: normal;
      font-weight: 700;
      line-height: 135%;
      text-transform: uppercase;
      color: #a7b1bd;

      @media screen and (max-width: 1100px) {
        font-size: 20px;
      }
    }

    &_icons {
      svg {
        width: 32px;
        height: 32px;
        fill: transparent;

        @media screen and (max-width: 1540px) {
          width: 24px;
          height: 24px;
        }
      }
      &_false {
        path {
          stroke: #c4cad3;
          fill: transparent;
        }
      }
      &_true {
        fill: #12c695;
        path {
          fill: #12c695;
        }
      }
    }
  }

  &_title {
    padding: 31px 0 40px;
    width: 100%;

    @media screen and (max-width: 1540px) {
      padding: 64px 0 57px;
    }
    @media screen and (max-width: 1100px) {
      padding: 47px 0 43px;
    }

    @media screen and (max-width: 860px) {
      padding: 32px 0 39px;
      background-color: #ffffff;
    }

    &_switcher {
      // width: calc(100%/5);
      padding: 0 16px;
      @media screen and (max-width: 860px) {
        & > .switcher,
        & p,
        & button,
        & .promocode {
          display: none;
        }
      }

      p {
        max-width: 240px;
        text-align: center;
        font-family: "ITC Avant Garde Gothic Std",serif;
        font-size: 16px;
        font-style: normal;
        font-weight: 300;
        line-height: 175%;
        margin: 15px auto 0;
        span {
          color: #40acff;
        }
        @media screen and (max-width: 1540px) {
          font-size: 12px;
        }
        @media screen and (max-width: 1100px) {
          font-size: 14px;
        }
      }
    }
    &_planColumn {
      //width: calc(100%/5);
      position: relative;
      padding-top: 62px;
      width: 100%;

      @media screen and (max-width: 1540px) {
        padding-top: 40px;
        // width: calc((100% - 270px) / 4 );
      }
      @media screen and (max-width: 1100px) {
        padding-top: 35px;
        // width: calc((100% - 210px) / 4 );
      }
      @media screen and (max-width: 860px) {
        //width: 160px;
      }

      &_trial {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        border-radius: 30px 30px 0 0;
        background-color: #38317f;
        font-family: "ITC Avant Garde Gothic Std",serif;
        font-size: 18px;
        font-style: normal;
        font-weight: 600;
        line-height: 135%;
        text-transform: capitalize;
        color: #ffffff;
        padding: 19px 10px;
        text-align: center;

        @media screen and (max-width: 1540px) {
          padding: 10px;
          font-size: 14px;
        }

        @media screen and (max-width: 1100px) {
          padding: 8px;
          border-radius: 16px 16px 0 0;
        }
        @media screen and (max-width: 860px) {
          font-size: 12px;
        }
      }

      &_with_trial {
        &:before {
          content: "";
          position: absolute;
          left: 0;
          top: 62px;
          bottom: 0;
          width: 2px;
          background: linear-gradient(
            180deg,
            rgba(196, 202, 211, 1) 0%,
            rgba(196, 202, 211, 0) 100%
          );

          @media screen and (max-width: 1540px) {
            top: 38px;
          }

          @media screen and (max-width: 860px) {
            top: 32px;
          }
        }

        &:after {
          content: "";
          position: absolute;
          right: 0;
          top: 62px;
          bottom: 0;
          width: 2px;
          background: linear-gradient(
            180deg,
            rgba(196, 202, 211, 1) 0%,
            rgba(196, 202, 211, 0) 100%
          );

          @media screen and (max-width: 1540px) {
            top: 38px;
          }
          @media screen and (max-width: 860px) {
            top: 32px;
          }
        }
      }

      &_title {
        padding-top: 24px;
        text-align: center;
        font-family: "ITC Avant Garde Gothic Std",serif;
        font-size: 28px;
        font-style: normal;
        font-weight: 500;
        line-height: normal;
        color: #a7b1bd;
        @media screen and (max-width: 1540px) {
          font-size: 18px;
        }
        @media screen and (max-width: 1100px) {
          padding-top: 10px;
          font-size: 16px;
        }
      }
      &_price {
        padding: 17px 0 33px;
        display: flex;
        align-items: center;
        justify-content: center;

        @media screen and (max-width: 1540px) {
          padding: 22px 0 35px;
        }
        @media screen and (max-width: 1100px) {
          padding: 12px 0 18px;
        }
        @media screen and (max-width: 860px) {
          padding: 12px 0 14px;
        }

        span {
          margin-left: 8px;

          &:nth-child(1) {
            color: #a7b1bd;
            font-family: "ITC Avant Garde Gothic Std",serif;
            font-size: 22px;
            font-style: normal;
            font-weight: 300;
            line-height: 160%;
            margin-left: 0;
            text-align: center;
            position: relative;
            &:before {
              content: "";
              width: 100%;
              position: absolute;
              top: 16px;
              transform: rotate(-20deg);
              height: 2px;
              background-color: #a7b1bd;

              @media screen and (max-width: 1540px) {
                top: 11px;
              }
            }
            @media screen and (max-width: 1540px) {
              font-size: 16px;
            }
            @media screen and (max-width: 1100px) {
              font-size: 14px;
            }
          }
          &:nth-child(2) {
            font-family: "Bebas Neue Pro",serif;
            font-size: 48px;
            font-style: normal;
            font-weight: 700;
            line-height: 100%;
            text-transform: uppercase;

            @media screen and (max-width: 1540px) {
              font-size: 40px;
            }
            @media screen and (max-width: 1100px) {
              font-size: 28px;
            }
          }
          &:nth-child(3) {
            color: #a7b1bd;
            font-family: "ITC Avant Garde Gothic Std",serif;
            font-size: 16px;
            font-style: normal;
            font-weight: 300;
            line-height: 175%;

            @media screen and (max-width: 1540px) {
              font-size: 12px;
            }
          }
        }
      }
      &_button {
        display: flex;
        align-items: center;
        justify-content: center;

        button {
          padding: 19px 38px;
          background-color: #40acff;
          border: none;
          outline: none;
          border-radius: 100px;
          font-family: "Bebas Neue Pro",serif;
          font-size: 24px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          letter-spacing: 3.6px;
          text-transform: uppercase;
          color: #fff;
          transition: all 0.3s;
          cursor: pointer;
          &:hover {
            background-color: #38317f;
          }

          @media screen and (max-width: 1540px) {
            padding: 16px 24px;
            font-size: 18px;
          }
          @media screen and (max-width: 1100px) {
            padding: 14px 17px;
            font-size: 16px;
          }
        }
      }
    }
  }

  &_mobile_switcher {
    display: none;

    @media screen and (max-width: 860px) {
      padding: 40px 0 0;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-wrap: wrap;
      max-width: 250px;
      margin: 0 auto;
    }

    p {
      text-align: center;
      font-family: "ITC Avant Garde Gothic Std",serif;
      font-size: 16px;
      font-style: normal;
      font-weight: 300;
      line-height: 175%;
      margin: 8px auto 0;
      span {
        color: #40acff;
      }
    }
  }
}
</style>
